import React, { useEffect, ReactNode, useRef, useMemo } from 'react';
import FocusTrap from 'focus-trap-react';
import styles from './ModalDialog.module.scss';
import Div from '../Element/Div/Div';
import Backdrop from '../Backdrop/Backdrop';
import Typography from '../Typography/Typography';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import Flex from '../Flex/Flex';
import Gutter from '../Gutter/Gutter';
import Breakpoint from '../Breakpoint/Breakpoint';
import usePreventScroll from '../../shared/utils/usePreventScroll/usePreventScroll';
import { CreateProps } from '../../types/utils/CreateProps';
import { ArrayElementType } from '../../types/utils/ArrayElementType';
import { ContainerContext } from '../../shared/utils/contexts';

type ModalDialogProps = CreateProps<
  {
    /** Opens and closes the modalDialog   */
    isOpen?: boolean;
    /** Title of modalDialog  */
    title: string;
    /**  Button text of accept button, if the text is not given the Button isn't rendered  */
    acceptButtonText?: string;
    /**  Button text of close button, if the text is not given the Button isn't rendered  */
    closeButtonText?: string;
    /**  Callback fired when user click acceptButton */
    onAcceptClick?: () => void;
    /**  Callback fired when user clicks closeButton, backdrop, close icon or presses Esc */
    onCloseClick?: () => void;
    /**  The content of modalDialog dialog  */
    children?: ReactNode;
    /** Toggle accept button to be disabledg   */
    acceptButtonDisabled?: boolean;
    /** Toggle close button to be disabled  */
    closeButtonDisabled?: boolean;
    /**  ClassName for accept button  */
    acceptButtonClassName?: string;
    /**  ClassName for close button  */
    closeButtonClassName?: string;
    /**  Area label for close icon  */
    closeIconAreaLabel?: string;
    /**  Destructive style for modalDialog  */
    destructive?: boolean;
    /**  Size of modal dialog  */
    size?: SupportedModalSizes;
    noWindow?: boolean; // Not to be used in product teams! This prop is only for internal testing purposes, used by ds-core team in jest tests.
  },
  typeof Div
>;
const ModalDialog: React.FunctionComponent<ModalDialogProps> = ({
  isOpen,
  title,
  acceptButtonText,
  closeButtonText,
  acceptButtonDisabled,
  closeButtonDisabled,
  acceptButtonClassName,
  closeButtonClassName,
  onAcceptClick,
  onCloseClick,
  children,
  size = 'md',
  destructive,
  closeIconAreaLabel,
  noWindow,
  ...props
}) => {
  usePreventScroll(!!isOpen);

  useEffect(() => {
    const closeKeyHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen === true) {
        onCloseClick && onCloseClick();
      }
    };
    document.addEventListener('keydown', closeKeyHandler);
    return () => document.removeEventListener('keydown', closeKeyHandler);
  }, [onCloseClick, isOpen]);
  const innerContainerRef =
    noWindow || typeof window === 'undefined'
      ? undefined
      : useRef<HTMLElement>(document.body);
  const ctxValue = useMemo(() => {
    return {
      getContainer() {
        return innerContainerRef?.current;
      },
    };
  }, []);

  return (
    <Div>
      {isOpen && (
        <Div className={styles.modalDialog} aria-modal="true">
          <FocusTrap>
            <div className={styles.modalDialog_focustrap_inner_container}>
              <Backdrop isVisible={true} onCloseClick={onCloseClick} />
              <Div className={styles.modalDialog_hiddenFocus} tabIndex={0} />

              <Div
                {...props}
                className={
                  props.className
                    ? props.className + ' ' + styles.modalDialog_container
                    : styles.modalDialog_container
                }
              >
                <Div
                  borderRadius={{ size: 'lg' }}
                  boxShadow="lg"
                  padding={{ bottom: 'md' }}
                  refElement={innerContainerRef}
                  className={[
                    styles.modalDialog_inner_container,
                    { [styles.modalDialog_inner_container_lg]: size == 'lg' },
                  ]}
                >
                  <Flex
                    className={styles.modalDialog_header_section}
                    spaceBetween
                    occupyVerticalSpace={false}
                    padding={{ right: 'sm', top: 'sm' }}
                  >
                    <Div
                      className={styles.header}
                      padding={{
                        horizontal: 'lg',
                        bottom: 'lg',
                        top: 'sm',
                      }}
                    >
                      <header>
                        <Typography
                          font="teliasans"
                          fontWeight="medium"
                          tagName="h2"
                          color="black"
                          className={styles.modalDialog_title}
                        >
                          {title}
                        </Typography>
                      </header>
                    </Div>
                    <Breakpoint narrowerThan={'md'}>
                      <Icon
                        onClick={onCloseClick}
                        area-label={closeIconAreaLabel}
                        onKeyPress={(e) => {
                          e.key === 'Enter' && onCloseClick && onCloseClick();
                        }}
                        tabIndex={0}
                        clickableAppearance
                        className={styles.modalDialog_container_icon}
                        name="close"
                        size="sm"
                        color="gray500"
                      />
                    </Breakpoint>
                    <Breakpoint widerThan={'md'}>
                      <Icon
                        onClick={onCloseClick}
                        onKeyPress={(e) => {
                          e.key === 'Enter' && onCloseClick && onCloseClick();
                        }}
                        tabIndex={0}
                        clickableAppearance
                        className={[
                          'modalDialog-icon-mobile-test',
                          styles.modalDialog_container_icon,
                        ]}
                        hoverColor="purple500"
                        name="close"
                        size="md"
                        color="gray500"
                      />
                    </Breakpoint>
                  </Flex>
                  <Div className={styles.modalDialog_content_section}>
                    {children && (
                      <Div
                        padding={{ horizontal: 'lg', bottom: 'sm' }}
                        className={styles.modalDialog_container_content}
                        color="black"
                      >
                        <ContainerContext.Provider value={ctxValue as any}>
                          {children}
                        </ContainerContext.Provider>
                      </Div>
                    )}
                  </Div>
                  {(closeButtonText || acceptButtonText) && (
                    <Div className={styles.modalDialog_footer_section}>
                      <Flex
                        padding={{ top: 'md', horizontal: 'lg' }}
                        responsiveHidden={{ thinnerThan: 'sm' }}
                        alignRight
                        centeredVertically
                      >
                        {closeButtonText && (
                          <Button
                            className={closeButtonClassName}
                            variant="text"
                            onClick={onCloseClick}
                            disabled={closeButtonDisabled}
                            size="lg"
                          >
                            {closeButtonText}
                          </Button>
                        )}

                        {acceptButtonText && (
                          <>
                            <Gutter size="md" />

                            <Button
                              className={acceptButtonClassName}
                              size="sm"
                              onClick={onAcceptClick}
                              variant={destructive ? 'destructive' : 'primary'}
                              disabled={acceptButtonDisabled}
                            >
                              {acceptButtonText}
                            </Button>
                          </>
                        )}
                      </Flex>
                      <Flex
                        padding={{ top: 'md', horizontal: 'md' }}
                        responsiveHidden={{ widerThan: 'xs' }}
                        directionColumn
                      >
                        {acceptButtonText && (
                          <Button
                            className={acceptButtonClassName}
                            size="sm"
                            onClick={onAcceptClick}
                            variant={destructive ? 'destructive' : 'primary'}
                            disabled={acceptButtonDisabled}
                          >
                            {acceptButtonText}
                          </Button>
                        )}

                        {closeButtonText && (
                          <>
                            <Gutter size="sm" />

                            <Button
                              className={closeButtonClassName}
                              variant="text"
                              onClick={onCloseClick}
                              disabled={closeButtonDisabled}
                              size="lg"
                            >
                              {closeButtonText}
                            </Button>
                          </>
                        )}
                      </Flex>
                    </Div>
                  )}
                </Div>
              </Div>
            </div>
          </FocusTrap>
        </Div>
      )}
    </Div>
  );
};

export const supportedModalSizes = ['md', 'lg'] as const;
export type SupportedModalSizes = ArrayElementType<typeof supportedModalSizes>;
export default ModalDialog;
